"use client";

import { useMemo, type AnchorHTMLAttributes, type ReactNode } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type {
  CAdvertisementsCategory,
  CAdvertisementsContent,
  CAdvertisementsItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { Field, Input, Select, SubmitButton } from "@/components/ui/Form";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { cAdvertisementsTranslations, type CAdvertisementsTranslations } from "./CAdvertisements.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CAdvertisements.module.css";

/**
 * CAdvertisements - publiczna strona-rozdzielacz ogloszen (`c_advertisements`).
 *
 * **Data source:** `C_ADVERTISEMENTS_CONTROLLER::getApiContent()` zwraca:
 *   - `view === 'list'`  -> drzewo podkategorii (c_category_advertisement) +
 *     formularz wyszukiwania
 *   - `view === 'search'` -> paginowana lista ogloszen pasujacych do filtrow
 *     agregowana ze wszystkich podkategorii (po submit'cie formularza)
 *
 * Pojedyncze ogloszenie / lista w podkategorii to OSOBNY komponent
 * `c_category_advertisement` - tu tylko nawigacja kategoriami + wyszukiwarka.
 *
 * **Security:** `text` / `short_text` to HTML z admin panelu (trusted) - OK
 * dla `dangerouslySetInnerHTML` (spojnie z CNews/CBlog).
 */
export function CAdvertisements({ content }: CAdvertisementsProps) {
  const t = useT(cAdvertisementsTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <SearchForm content={content} t={t} />

      {content.view === "search" ? (
        <SearchResults content={content} t={t} />
      ) : (
        <CategoryTree categories={content.categories ?? []} t={t} />
      )}
    </div>
  );
}

export type CAdvertisementsProps = {
  content: CAdvertisementsContent;
};

/* Search form (RHF + Zod) */

function buildSearchSchema(t: CAdvertisementsTranslations) {
  /* Pola search sa opcjonalne. Walidacja sprawdza jedynie format ceny -
     bo niepoprawna liczba na backendzie odfiltruje wynik bez sygnalu dla usera. */
  const optionalPrice = z
    .string()
    .trim()
    .refine((v) => v === "" || /^\d+([.,]\d{1,2})?$/.test(v), {
      message: t.validation.invalidPrice,
    });

  return z.object({
    keyword: z.string().trim(),
    type_id: z.string(),
    price_from: optionalPrice,
    price_to: optionalPrice,
  });
}

type SearchFormValues = z.infer<ReturnType<typeof buildSearchSchema>>;

function SearchForm({
  content,
  t,
}: {
  content: CAdvertisementsContent;
  t: CAdvertisementsTranslations;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const schema = useMemo(() => buildSearchSchema(t), [t]);
  const initial = content.search;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SearchFormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      keyword: initial?.keyword ?? "",
      type_id: initial?.type_id != null ? String(initial.type_id) : "",
      price_from: initial?.price_from != null ? String(initial.price_from) : "",
      price_to: initial?.price_to != null ? String(initial.price_to) : "",
    },
  });

  const onSubmit = (values: SearchFormValues) => {
    const qs = new URLSearchParams();
    qs.set("search_submitted", "1");
    if (values.keyword !== "") qs.set("keyword", values.keyword);
    if (values.type_id !== "") qs.set("type_id", values.type_id);
    /* Normalizujemy decimal: backend (IFINANCE::getPrice) i tak parsuje
       przecinek na kropke, ale wysylamy juz kropke zeby URL byl czytelny. */
    if (values.price_from !== "")
      qs.set("price_from", values.price_from.replace(",", "."));
    if (values.price_to !== "")
      qs.set("price_to", values.price_to.replace(",", "."));
    router.push(`${pathname}?${qs.toString()}`);
  };

  return (
    <section className={styles.searchSection} aria-labelledby="cadv-search-title">
      <h2 id="cadv-search-title" className={styles.searchTitle}>
        {t.searchTitle}
      </h2>
      <form
        className={styles.form}
        role="search"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className={styles.fieldsGrid}>
          <Field label={t.fields.keyword} className={styles.gridSpan2}>
            <Input type="search" autoComplete="off" {...register("keyword")} />
          </Field>
          <Field label={t.fields.type}>
            <Select placeholder={t.allTypes} {...register("type_id")}>
              {content.types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label={t.fields.priceFrom}
            error={errors.price_from?.message}
          >
            <Input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              {...register("price_from")}
            />
          </Field>
          <Field
            label={t.fields.priceTo}
            error={errors.price_to?.message}
          >
            <Input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              {...register("price_to")}
            />
          </Field>
        </div>
        <div className={styles.formActions}>
          <SubmitButton>{t.searchBtn}</SubmitButton>
        </div>
      </form>
    </section>
  );
}

/* Category tree (default view) */

function CategoryTree({
  categories,
  t,
}: {
  categories: CAdvertisementsCategory[];
  t: CAdvertisementsTranslations;
}) {
  if (categories.length === 0) {
    return <div className={styles.empty}>{t.noCategories}</div>;
  }

  return (
    <section className={styles.treeSection} aria-labelledby="cadv-tree-title">
      <h2 id="cadv-tree-title" className={styles.treeTitle}>
        {t.categoriesTitle}
      </h2>
      <ul className={styles.tree}>
        {categories.map((cat) => (
          <CategoryNode key={cat.id} category={cat} t={t} />
        ))}
      </ul>
    </section>
  );
}

function CategoryNode({
  category,
  t,
}: {
  category: CAdvertisementsCategory;
  t: CAdvertisementsTranslations;
}) {
  const hasChildren = category.children.length > 0;
  return (
    <li className={styles.treeNode} data-level={category.level}>
      <SmartLink
        href={category.href}
        className={hasChildren ? styles.parentLink : styles.leafLink}
      >
        <span className={styles.nodeName}>{category.name}</span>
        {!hasChildren && (
          <span className={styles.nodeCount}>
            {t.adsCount(category.count_adverts)}
          </span>
        )}
      </SmartLink>
      {hasChildren && (
        <ul className={styles.treeChildren}>
          {category.children.map((child) => (
            <CategoryNode key={child.id} category={child} t={t} />
          ))}
        </ul>
      )}
    </li>
  );
}

/* Search results view */

function SearchResults({
  content,
  t,
}: {
  content: CAdvertisementsContent;
  t: CAdvertisementsTranslations;
}) {
  const items: CAdvertisementsItem[] = Array.isArray(content.items) ? content.items : [];
  const totalCount = content.pagination?.total_count ?? 0;

  if (items.length === 0) {
    return <div className={styles.empty}>{t.noAdsFound}</div>;
  }

  return (
    <section className={styles.resultsSection} aria-labelledby="cadv-results-title">
      <h2 id="cadv-results-title" className={styles.resultsTitle}>
        {t.resultsTitle(totalCount)}
      </h2>
      <ul className={styles.list}>
        {items.map((item) => (
          <ResultCard key={item.id} item={item} t={t} />
        ))}
      </ul>
    </section>
  );
}

function ResultCard({
  item,
  t,
}: {
  item: CAdvertisementsItem;
  t: CAdvertisementsTranslations;
}) {
  /* short_text uzywany jako teaser; gdy pusty - fragment z tekstu pelnego */
  const teaserHtml = item.short_text || item.text || "";
  return (
    <li className={styles.card}>
      <SmartLink href={item.href} className={styles.cardLink} aria-label={item.name}>
        <div className={styles.thumbWrap}>
          {item.picture ? (
            <img
              src={item.picture.thumb_src}
              alt={item.picture.name}
              width={item.picture.width ?? undefined}
              height={item.picture.height ?? undefined}
              className={styles.thumb}
              loading="lazy"
            />
          ) : (
            <ImagePlaceholder />
          )}
        </div>
        <div className={styles.body}>
          <h3 className={styles.cardTitle}>{item.name}</h3>
          <div className={styles.meta}>
            {item.type_name && (
              <span className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.fields.type}:</span> {item.type_name}
              </span>
            )}
            {item.add_date && (
              <span className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.addDate}:</span> {item.add_date}
              </span>
            )}
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>{t.price}:</span>{" "}
              {item.negotiated_price
                ? t.negotiatedPrice
                : item.price != null
                  ? t.formatPrice(item.price)
                  : t.priceNotSet}
            </span>
          </div>
          {teaserHtml && (
            /* <div> nie <p> - tekst bywa HTML (`<p>...`) -> uniknięcie
               zagnieżdżonych <p> (hydration mismatch); spojnie z CNews/CBlog. */
            <div
              className={styles.teaser}
              dangerouslySetInnerHTML={{ __html: teaserHtml }}
            />
          )}
          <span className={styles.more}>{t.readMore}</span>
        </div>
      </SmartLink>
    </li>
  );
}

/* Helpers */

/**
 * Link wewnetrzny przez `<Link>` (SPA nav) lub natywny `<a>` dla URL-i
 * cross-domain. Skopiowane z CNews/CBlog/CSearch.
 */
function SmartLink({
  href,
  className,
  children,
  ...rest
}: {
  href: string;
  className?: string;
  children: ReactNode;
} & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const internal = extractInternalHref(href);
  if (internal !== null) {
    return (
      <Link href={internal} className={className} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} {...rest}>
      {children}
    </a>
  );
}
